// @flow
import * as React from 'react';
import {
  PageTitle, SalesQualifiedFutureQuery, SalesQualifiedQuery, useSalesQualifiedFutureQuery, useSalesQualifiedQuery,
  useSessionStorageState
} from '../../../../shared';
import { Stack } from '@mui/material';
import { TagSelector } from '../../../../features';
import {
  ClientViewSortInput,
  Field,
  PageInput,
  SortDirection
} from '@coverright/data-access/types/enrollment';
import { SearchInput } from '@coverright/ui/inputs';
import { DashboardDataGrid } from '../../ui/dashboard-data-grid';
import { useEffect } from 'react';

type Filters = {searchTerm: string, includedTagsIds: string[]};
const initialFilters: Filters = {searchTerm: '', includedTagsIds: []};

type Props = {
  title: string,
  suffix: string,
  agent?: string,
  dataSource: typeof useSalesQualifiedFutureQuery | typeof useSalesQualifiedQuery,
}

export const SalesQualified = (props: Props) => {
  const {dataSource, suffix, title, agent} = props
  const [filters, setFilters] = useFilters(suffix);
  const [sort, setSort] = useSort(suffix);
  const [page, setPage] = usePage(suffix);

  useEffect(() => {
    setPage(p => ({...p, page: 0}))
    setFilters(initialFilters)
  }, [agent]);

  const {data: newData, previousData, loading} = dataSource({
    fetchPolicy: 'cache-and-network',
    variables: {
      agentId: agent,
      pageInput: page,
      sort,
      ...filters
    }
  });

  const data = newData || previousData;
  const clients = isTodayQuery(data) ? data?.salesQualified?.data : data?.salesQualifiedFuture?.data;

  const total = isTodayQuery(data) ? data?.salesQualified?.totalElements : data?.salesQualifiedFuture?.totalElements;

  return (
    <>
      <PageTitle title={title}>
        <Stack direction={'row'} gap={2}>
          <SearchInput value={filters.searchTerm}
                       loading={loading}
                       onChange={searchTerm => setFilters(p => ({...p, searchTerm}))} />

          <TagSelector selectedValues={filters.includedTagsIds}
                       className={'mt-0'}
                       onChange={tags => setFilters(p => ({...p, includedTagsIds: tags.map(t => t.id)}))} />
        </Stack>
      </PageTitle>

      <DashboardDataGrid page={page.page}
                         rowsPerPage={page.size}
                         total={total || 0}
                         loading={loading}
                         sort={sort}
                         onSortModelChange={setSort}
                         onPageChange={p => setPage(prev => ({...prev, page: p }))}
                         onRowsPerPageChange={p => setPage(prev => ({page: 0, size: p }))}
                         rows={clients || []}/>
    </>
  );
};

const isTodayQuery = (data: SalesQualifiedQuery | SalesQualifiedFutureQuery | undefined): data is SalesQualifiedQuery => (
  data !== undefined &&
  'salesQualified' in data &&
  typeof data.salesQualified === 'object' &&
  data.salesQualified !== null &&
  Array.isArray(data.salesQualified.data)
)

const usePage = (suffix: string) => {
  return useSessionStorageState<PageInput>(
    'salesQualifiedPage' + suffix,
    { page: 0, size: 20 }
  );
};

const useFilters = (suffix = '') => {
  return useSessionStorageState<Filters>(
    'salesQualifiedFilters' + suffix,
    initialFilters
  );
};

const useSort = (suffix = '') => {
  return useSessionStorageState<ClientViewSortInput | undefined>(
    'salesQualifiedSort' + suffix,
    {field: Field.CreatedAt, direction: SortDirection.Desc}
  )
}
